import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';

const SearchInterface = ({ variant = 'hero', onSearch, initialFilters = {} }) => {
  const [searchQuery, setSearchQuery] = useState(initialFilters?.query || '');
  const [location, setLocation] = useState(initialFilters?.location || '');
  const [productType, setProductType] = useState(initialFilters?.productType || '');
  const [priceRange, setPriceRange] = useState(initialFilters?.priceRange || '');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const filtersRef = useRef(null);
  const locationRef = useRef(null);

  const productTypes = [
    { value: '', label: 'All Products' },
    { value: 'vegetables', label: 'Vegetables' },
    { value: 'fruits', label: 'Fruits' },
    { value: 'grains', label: 'Grains' },
    { value: 'dairy', label: 'Dairy' },
    { value: 'meat', label: 'Meat & Poultry' },
    { value: 'herbs', label: 'Herbs & Spices' }
  ];

  const priceRanges = [
    { value: '', label: 'Any Price' },
    { value: '0-25', label: 'Under $25' },
    { value: '25-50', label: '$25 - $50' },
    { value: '50-100', label: '$50 - $100' },
    { value: '100-200', label: '$100 - $200' },
    { value: '200-500', label: '$200 - $500' },
    { value: '500+', label: 'Over $500' }
  ];

  const locationSuggestions = [
    'California, CA',
    'Iowa, IA',
    'Illinois, IL',
    'Nebraska, NE',
    'Kansas, KS',
    'Texas, TX',
    'Minnesota, MN',
    'Wisconsin, WI'
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filtersRef?.current && !filtersRef?.current?.contains(event.target)) {
        setIsFiltersOpen(false);
      }
      if (locationRef?.current && !locationRef?.current?.contains(event.target)) {
        setIsLocationDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e?.preventDefault();
    const searchParams = {
      query: searchQuery,
      location,
      productType,
      priceRange
    };
    
    if (onSearch) {
      onSearch(searchParams);
    } else {
      // Default navigation to product listings
      const params = new URLSearchParams();
      Object.entries(searchParams)?.forEach(([key, value]) => {
        if (value) params?.append(key, value);
      });
      window.location.href = `/property-listings?${params?.toString()}`;
    }
  };

  const handleLocationSelect = (selectedLocation) => {
    setLocation(selectedLocation);
    setIsLocationDropdownOpen(false);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setLocation('');
    setProductType('');
    setPriceRange('');
  };

  const hasActiveFilters = searchQuery || location || productType || priceRange;

  if (variant === 'hero') {
    return (
      <div className="w-full max-w-4xl mx-auto">
        <form onSubmit={handleSearch} className="space-y-4">
          {/* Main Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Icon name="Search" size={24} className="text-secondary" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              placeholder="Search for vegetables, fruits, grains, or farm products..."
              className="block w-full pl-12 pr-4 py-4 text-lg border border-border rounded-lg 
                       focus:border-border-focus focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 
                       transition-all duration-200 ease-out bg-surface text-text-primary
                       placeholder-text-secondary shadow-elevation-1"
            />
          </div>

          {/* Filter Controls */}
          <div className="flex flex-wrap gap-3 items-center">
            {/* Location Filter */}
            <div className="relative" ref={locationRef}>
              <button
                type="button"
                onClick={() => setIsLocationDropdownOpen(!isLocationDropdownOpen)}
                className="flex items-center space-x-2 px-4 py-2 bg-surface border border-border 
                         rounded-md hover:bg-secondary-100 transition-all duration-200 ease-out
                         micro-interaction"
              >
                <Icon name="MapPin" size={16} />
                <span className="text-sm font-medium">
                  {location || 'Available in'}
                </span>
                <Icon 
                  name="ChevronDown" 
                  size={16} 
                  className={`transition-transform duration-200 ${
                    isLocationDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isLocationDropdownOpen && (
                <div className="absolute top-full mt-1 w-64 bg-surface rounded-md shadow-elevation-3 
                              border border-border z-dropdown">
                  <div className="p-2">
                    <input
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e?.target?.value)}
                      placeholder="Enter state or availability location..."
                      className="w-full px-3 py-2 border border-border rounded-md text-sm
                               focus:border-border-focus focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {locationSuggestions?.filter(suggestion => 
                        suggestion?.toLowerCase()?.includes(location.toLowerCase())
                      )?.map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          onClick={() => handleLocationSelect(suggestion)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-secondary-100 transition-colors duration-200"
                        >
                          {suggestion}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Product Type Filter */}
            <select
              value={productType}
              onChange={(e) => setProductType(e?.target?.value)}
              className="px-4 py-2 bg-surface border border-border rounded-md text-sm font-medium
                       focus:border-border-focus focus:ring-1 focus:ring-primary-500 
                       transition-all duration-200 ease-out"
            >
              {productTypes?.map((type) => (
                <option key={type?.value} value={type?.value}>
                  {type?.label}
                </option>
              ))}
            </select>

            {/* Price Range Filter */}
            <select
              value={priceRange}
              onChange={(e) => setPriceRange(e?.target?.value)}
              className="px-4 py-2 bg-surface border border-border rounded-md text-sm font-medium
                       focus:border-border-focus focus:ring-1 focus:ring-primary-500 
                       transition-all duration-200 ease-out"
            >
              {priceRanges?.map((range) => (
                <option key={range?.value} value={range?.value}>
                  {range?.label}
                </option>
              ))}
            </select>

            {/* Search Button and Advanced Filters on Same Line */}
            <div className="flex items-center space-x-2">
              <button
                type="submit"
                className="bg-primary text-white px-6 py-2 rounded-md text-sm font-medium
                         hover:bg-primary-700 transition-all duration-200 ease-out micro-interaction"
              >
                Search Products
              </button>
              
              <button
                type="button"
                onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                className="flex items-center space-x-2 px-4 py-2 bg-surface border border-border 
                         rounded-md hover:bg-secondary-100 transition-all duration-200 ease-out
                         micro-interaction"
              >
                <Icon name="SlidersHorizontal" size={16} />
                <span className="text-sm font-medium">More Filters</span>
              </button>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="flex items-center space-x-2 px-3 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors duration-200"
              >
                <Icon name="X" size={14} />
                <span>Clear</span>
              </button>
            )}
          </div>

          {/* Advanced Filters Panel */}
          {isFiltersOpen && (
            <div 
              ref={filtersRef}
              className="bg-surface border border-border rounded-lg p-6 shadow-elevation-2
                       progressive-disclosure"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Organic Certified
                  </label>
                  <select className="w-full px-3 py-2 border border-border rounded-md text-sm focus:border-border-focus focus:ring-1 focus:ring-primary-500">
                    <option value="">Any</option>
                    <option value="yes">Organic Only</option>
                    <option value="no">Conventional</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Quantity Available
                  </label>
                  <select className="w-full px-3 py-2 border border-border rounded-md text-sm focus:border-border-focus focus:ring-1 focus:ring-primary-500">
                    <option value="">Any Amount</option>
                    <option value="1-100kg">1-100 kg</option>
                    <option value="100-500kg">100-500 kg</option>
                    <option value="500kg-1ton">500 kg - 1 ton</option>
                    <option value="above-1ton">Above 1 ton</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Harvest Season
                  </label>
                  <select className="w-full px-3 py-2 border border-border rounded-md text-sm focus:border-border-focus focus:ring-1 focus:ring-primary-500">
                    <option value="">Any Season</option>
                    <option value="spring">Spring</option>
                    <option value="summer">Summer</option>
                    <option value="fall">Fall</option>
                    <option value="winter">Winter</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Search Button */}
          <div className="flex justify-center">
            <button
              type="submit"
              className="bg-primary text-white px-8 py-3 rounded-lg text-lg font-semibold
                       hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 
                       transition-all duration-200 ease-out micro-interaction shadow-elevation-2"
            >
              Search Products
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Compact variant for header
  return (
    <form onSubmit={handleSearch} className="flex items-center space-x-2">
      <div className="relative flex-1">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon name="Search" size={16} className="text-secondary" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value)}
          placeholder="Search farm products..."
          className="block w-full pl-9 pr-3 py-2 border border-border rounded-md text-sm
                   focus:border-border-focus focus:ring-1 focus:ring-primary-500 
                   transition-all duration-200 ease-out bg-surface text-text-primary
                   placeholder-text-secondary"
        />
      </div>
      <button
        type="submit"
        className="bg-primary text-white px-4 py-2 rounded-md text-sm font-medium
                 hover:bg-primary-700 transition-all duration-200 ease-out micro-interaction"
      >
        Search
      </button>
    </form>
  );
};

export default SearchInterface;